import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics 4 Component - Deferred loading for better performance
const GoogleAnalytics = ({ measurementId }) => {
  const location = useLocation();
  const isInitialized = useRef(false);
  const loadTimeout = useRef(null);

  // Deferred load function
  const loadGA = () => {
    if (isInitialized.current || !measurementId) return;
    if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
      isInitialized.current = true;
      return;
    }

    isInitialized.current = true;

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', measurementId, {
      send_page_view: false // We'll send page views manually for SPA
    });
  };

  // Load GA4 after 3 seconds or on first interaction
  useEffect(() => {
    if (!measurementId) return;

    // Load after 3 seconds delay
    loadTimeout.current = setTimeout(loadGA, 3000);

    // Or load on first user interaction
    const interactionHandler = () => {
      if (loadTimeout.current) {
        clearTimeout(loadTimeout.current);
      }
      loadGA();
    };

    const events = ['click', 'scroll', 'keydown', 'touchstart'];
    events.forEach(evt => {
      document.addEventListener(evt, interactionHandler, { once: true, passive: true });
    });

    return () => {
      if (loadTimeout.current) {
        clearTimeout(loadTimeout.current);
      }
      events.forEach(evt => {
        document.removeEventListener(evt, interactionHandler);
      });
    };
  }, [measurementId]);

  // Track page views on route change
  useEffect(() => {
    if (!measurementId || !window.gtag) return;

    // Send page view
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname + location.search
    });
  }, [location, measurementId]);

  return null;
};

export default GoogleAnalytics;
